// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.
import { Button, Tooltip } from '@mui/material';
import { type ExpandedState, createColumnHelper } from '@tanstack/react-table';
import {
  AssetBrand,
  PortfolioActionsMenu,
  WatchlistToggleButton,
} from '@shared/ui-kit/components';
import { Table } from '@shared/ui-kit/components/grouped-table';
import { classNames } from '@shared/ui-kit/utils/classnames';
import {
  type MyInvestments,
  type SubRows,
} from '@shared/data-access/interfaces/common/my-investment';
import { type WorkflowState } from '@shared/data-access/interfaces/redux/workflow-state';
import {
  type TradeInputParameters,
  OperationsClientGate,
} from '@advisor-app/features/operations';
import { OperationType } from '@shared/data-access/interfaces/operation-type';
import { type FC, useMemo } from 'react';
import { useAppSelector } from '@advisor-app/store';
import { selectAccountCurrency } from '@shared/data-access/slices/user';
import { useInvestmentPopup } from '@shared/tag-management/components/investment-popup';
import { useTranslation } from 'react-i18next';
import { Translation } from '@shared/ui-kit/providers/translation-provider/translation';

export interface InvestmentTableProps {
  readonly data: MyInvestments[];
  readonly initialExpanded?: ExpandedState;
  readonly onLoad: (flow: WorkflowState, data: SubRows[]) => void;
  readonly onExport: (row: any) => void;
  readonly onSendToTrader: (trade: TradeInputParameters) => void;
}

const columnHelper = createColumnHelper<MyInvestments>();

export const InvestmentTable: FC<InvestmentTableProps> = ({
  data,
  initialExpanded,
  onExport,
  onLoad,
  onSendToTrader,
}) => {
  const { openInvestmentPopup } = useInvestmentPopup();
  const { t } = useTranslation();

  const assetsClassMap = useMemo(
    () =>
      new Map(
        Object.entries({
          stock: t('glossary.stock', { ns: 'glossary' }),
          etf: t('glossary.etf', { ns: 'glossary' }),
          mutual_fund: t('glossary.mutual-fund', { ns: 'glossary' }),
          other: t('glossary.other', { ns: 'glossary' }),
        }),
      ),
    [t],
  );

  const currency = useAppSelector(selectAccountCurrency);

  const columns = useMemo(
    () => [
      columnHelper.accessor('asset_class', {
        id: 'asset_class',
        cell: ({ getValue, row }) => (
          <div className="flex items-center gap-x-2 truncate">
            <Table.Expander expander={row} />
            {row.getCanExpand() ? (
              <span>{assetsClassMap.get(getValue())}</span>
            ) : (
              <AssetBrand
                ticker={row.original.ticker!}
                assetType={row.original.asset_class}
                logoUrl={row.original.url_logo}
                url={row.original.url_morningstar}
                label={row.original.name!}
                analyzePopup={openInvestmentPopup}
                watchlistButton={
                  <WatchlistToggleButton
                    ticker={row.original.ticker!}
                    assetType={row.original.asset_class}
                  />
                }
              />
            )}
          </div>
        ),
        header: t('common.name', { ns: 'common' }),
        maxSize: 220,
      }),
      columnHelper.accessor('quantity', {
        header: t('common.quantity', { ns: 'common' }),
        meta: { alignment: 'end' },
        cell: ({ getValue }) => <Table.Data value={getValue()} format="decimal" />,
      }),
      columnHelper.accessor(row => row.local_currency_values?.currency, {
        header: t('common.currency', { ns: 'common' }),
        cell: ({ getValue }) => getValue() ?? currency,
      }),
      columnHelper.accessor(row => row.local_currency_values?.price as number, {
        header: t('common.price', { ns: 'common' }),
        meta: { alignment: 'end' },
        cell: ({ getValue, row }) => (
          <Table.Data
            value={getValue()}
            format="currency"
            safeValue=""
            formatOptions={{
              currency: row.original.local_currency_values?.currency ?? currency,
              currencySign: 'standard',
              maximumFractionDigits: 2,
              minimumFractionDigits: 2,
            }}
          />
        ),
      }),
      columnHelper.accessor('investment', {
        header: t('common.investment', { ns: 'common' }),
        meta: { alignment: 'end' },
        cell: ({ getValue, row }) => (
          <Table.Data
            value={
              row.getCanExpand()
                ? getValue()
                : row.original.local_currency_values?.outstanding_balance
            }
            format="currency"
            formatOptions={{
              currency: row.original.local_currency_values?.currency ?? currency,
              currencySign: 'standard',
            }}
          />
        ),
      }),
      columnHelper.accessor('total_gain_loss', {
        header: t('common.gain-loss', { ns: 'common' }),
        sortingFn: 'gainLoss',
        meta: { alignment: 'end' },
        cell: ({ getValue, row }) => (
          <span
            className={classNames({
              profit: getValue()[0] >= 0,
              loss: getValue()[0] < 0,
            })}
          >
            <Table.Data
              value={
                row.getCanExpand()
                  ? getValue()[0]
                  : row.original.local_currency_values.unrealized_capital_gains
              }
              format="currency"
              formatOptions={{
                currency: row.original.local_currency_values?.currency ?? currency,
                currencySign: 'standard',
              }}
            />{' '}
            (
            <Table.Data
              value={
                row.getCanExpand()
                  ? getValue()[0] / row.original.investment
                  : row.original.local_currency_values.unrealized_capital_gains /
                    row.original.local_currency_values?.outstanding_balance
              }
              format="percentage"
            />
            )
          </span>
        ),
      }),
      columnHelper.display({
        id: 'options',
        enableSorting: false,
        cell: ({ row }) => (
          <div
            className="flex justify-end gap-x-1 aria-expanded:justify-between"
            aria-expanded={row.getCanExpand()}
          >
            {row.original.asset_class === 'other' ? null : row.getCanExpand() ? (
              <PortfolioActionsMenu
                analyze
                taxManager
                rebalance
                download
                onAnalyze={() =>
                  onLoad(
                    { type: 'load', asset: row.original.asset_class as any },
                    row.original.subRows,
                  )
                }
                onTaxManager={() =>
                  onLoad(
                    {
                      type: 'tax-harvesting',
                      asset: row.original.asset_class as any,
                    },
                    row.original.subRows,
                  )
                }
                onRebalance={() =>
                  onLoad(
                    {
                      type: 'rebalance',
                      asset: row.original.asset_class as any,
                    },
                    row.original.subRows,
                  )
                }
                onDownload={() => onExport(row.original)}
              />
            ) : (
              <OperationsClientGate>
                <Tooltip title={t('dashboard.sell-position', { ns: 'dashboard' })}>
                  <span>
                    <Button
                      variant="outlined"
                      color="secondary"
                      size="small"
                      data-testid="test-sell-investment"
                      onClick={() =>
                        onSendToTrader({
                          operation: OperationType.SELL,
                          ticker: row.original.ticker!,
                          name: row.original.name,
                          volume: row.original.investment,
                          tickerInfo: row.original,
                          sourceMyInvestments: true,
                          unrealized_capital_gains: (row.original as any)
                            .unrealized_capital_gains,
                        })
                      }
                    >
                      <Translation i18nKey="dashboard.sell" ns="dashboard" />
                    </Button>
                  </span>
                </Tooltip>
              </OperationsClientGate>
            )}
          </div>
        ),
      }),
    ],
    [
      assetsClassMap,
      currency,
      onExport,
      onLoad,
      onSendToTrader,
      openInvestmentPopup,
      t,
    ],
  );

  return (
    <div className="overflow-x-auto">
      <Table.Grouped
        data={data}
        columns={columns}
        className="max-h-[320px]"
        initialExpanded={initialExpanded}
      />
    </div>
  );
};
